import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import { Logo } from "@/components/Logo";
import { Loader2, Building2, Camera, Check } from "lucide-react";

// (2026-02) Login wayfinding fix — the old "Freelancer or Production Partner?" hint was a
// single tiny text link at the very bottom of the page, easy to miss for a newcomer landing
// here by default. Equal-weight "doors" sit right under the page title instead: NJM Staff
// (this page, already selected), Freelancer -> /build/login (NJM Build's own Google login).
// Job A (2026-10, login cleanup) — the "Production Partner" door was removed from this
// chooser (not the only route to /partner-portal/login: BuildLogin.jsx already carries its
// own "Production Partner? Sign in with a secure email link →" fallback, so partners aren't
// stranded). No route/API/auth change — see PR notes.
const LOGIN_DOORS = [
  { key: "staff", label: "NJM Staff", desc: "Google Workspace", icon: Building2, href: null },
  { key: "freelancer", label: "Freelancer", desc: "NJM Build sign-in", icon: Camera, href: "/build/login" },
];

const QUICK = [
  { email: "founder@njm.local", role: "Founder", pw: "founder123" },
  { email: "pm@njm.local", role: "PM", pw: "pm123" },
  { email: "finance@njm.local", role: "Finance", pw: "finance123" },
  { email: "creative@njm.local", role: "Creative", pw: "creative123" },
  { email: "guest@njm.local", role: "Guest", pw: "guest123" },
];

const GOOGLE_ERROR_LABELS = {
  domain_not_allowed: "Only your organisation's Google Workspace accounts can sign in.",
  email_not_verified: "That Google account's email isn't verified.",
  invalid_or_expired_state: "That sign-in link expired — please try again.",
  google_denied: "Google sign-in was cancelled.",
  google_auth_failed: "Something went wrong signing in with Google — please try again.",
  account_deactivated: "This account has been deactivated. Contact your NJM administrator.",
};

export default function Login() {
  const { login, completePasswordReset } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(GOOGLE_ERROR_LABELS[params.get("error")] || "");
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  // PM-account password auth (2026-02) — set once /auth/login responds with
  // `must_reset_password: true`; while set, the form below renders the forced "choose a new
  // password" step instead of the normal email/password form.
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // PM-account password auth (2026-02) — PM accounts sign in with a password even when the org
  // as a whole is on Google SSO (AUTH_MODE=google): PMs aren't required to hold a Google
  // Workspace seat. This toggle reveals the same /auth/login password form Google mode
  // otherwise hides.
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => { api.get("/auth/config").then((r) => setConfig(r.data)).catch(() => setConfig({ mode: "demo", google_configured: false })); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      if (u?.must_reset_password) { setResetToken(u.reset_token); return; }
      nav(u?.role === "freelancer" ? "/build/me" : u?.role === "production_partner" ? "/partner/me" : "/");
    }
    catch (ex) { console.error("LOGIN_DEBUG", ex, ex?.response?.status, ex?.response?.data); const code = ex?.response?.data?.detail; setErr(GOOGLE_ERROR_LABELS[code] || code || "Login failed"); }
    finally { setBusy(false); }
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault();
    setErr("");
    if (newPassword.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (newPassword !== confirmPassword) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const u = await completePasswordReset(resetToken, newPassword);
      nav(u?.role === "freelancer" ? "/build/me" : u?.role === "production_partner" ? "/partner/me" : "/");
    }
    catch (ex) { const code = ex?.response?.data?.detail; setErr(code || "Couldn't set your new password — please try again."); }
    finally { setBusy(false); }
  };

  const signInWithGoogle = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
  };

  const isGoogleMode = config?.mode === "google";

  return (
    <div className="min-h-screen w-full flex flex-col lg:grid lg:grid-cols-2" data-testid="platform-login-page">
      {/* Brand panel — deep black, gold wordmark, cinematic B&W production still, giant watermark motif */}
      <div className="relative h-64 sm:h-72 lg:h-auto overflow-hidden bg-ink-deep flex flex-col justify-between p-6 sm:p-12 lg:p-16">
        <img
          src="https://customer-assets.emergentagent.com/job_8bf14107-78d9-4608-90c2-1e48ddc7f859/artifacts/fzsdeueb_IMG_3156_jpg.jpeg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "center 30%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/75 to-ink-deep/35" />
        <div className="relative z-10">
          <Logo theme="dark" size="xl" iconOnly testId="platform-login-logo" />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-editorial text-2xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] sm:leading-[1.05] text-white uppercase tracking-tight">
            One Platform.<br className="hidden lg:block" /> One Vision.
          </h1>
          <p className="mt-3 sm:mt-4 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-brand">Production Intelligence System</p>
        </div>
      </div>

      {/* Sign-in panel */}
      <div className="flex items-start lg:items-center justify-center bg-canvas px-4 py-10 sm:py-14">
        <div className="w-full max-w-[380px]">
          <div className="mb-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-faint">Welcome back</div>
            <h2 className="font-editorial text-2xl text-ink mt-1">Sign in to NJM Platform</h2>
          </div>

          <div className="mb-8" data-testid="login-doors">
            <div className="mb-2.5 text-[11px] font-medium uppercase tracking-wider text-faint">I am a…</div>
            <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
              {LOGIN_DOORS.map((d) => {
                const Icon = d.icon;
                const isActive = !d.href;
                const Wrapper = d.href ? "a" : "div";
                return (
                  <Wrapper
                    key={d.key}
                    href={d.href || undefined}
                    data-testid={`login-door-${d.key}`}
                    className={`relative flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3.5 text-center transition-all ${
                      isActive
                        ? "border-brand bg-brand/10 cursor-default"
                        : "border-hairline bg-white hover:border-brand hover:shadow-sm cursor-pointer"
                    }`}
                  >
                    {isActive && (
                      <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand">
                        <Check className="h-2.5 w-2.5 text-ink" strokeWidth={3} />
                      </span>
                    )}
                    <Icon className={`h-5 w-5 ${isActive ? "text-brand-dim" : "text-ink-soft"}`} strokeWidth={1.75} />
                    <div className="leading-tight">
                      <div className={`text-[11px] font-semibold ${isActive ? "text-ink" : "text-ink-soft"}`}>{d.label}</div>
                      <div className="text-[9.5px] text-faint mt-0.5">{d.desc}</div>
                    </div>
                  </Wrapper>
                );
              })}
            </div>
          </div>

          {!config ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-faint" /></div>
          ) : resetToken ? (
            <form onSubmit={submitNewPassword} className="space-y-4" data-testid="set-new-password-form">
              <p className="text-xs text-ink-soft leading-relaxed">
                First time signing in — choose a password only you know. You won't need your temporary password again.
              </p>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">New password</label>
                <input
                  data-testid="set-password-input"
                  type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                  className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">Confirm new password</label>
                <input
                  data-testid="set-password-confirm-input"
                  type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
                />
              </div>
              {err && <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
              <button
                data-testid="set-password-submit"
                disabled={busy}
                className="w-full rounded-md bg-brand py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors"
              >{busy ? "Saving…" : "Set password & sign in"}</button>
            </form>
          ) : isGoogleMode && !showPasswordForm ? (
            <div className="space-y-4">
              {err && <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
              {config.google_configured ? (
                <button
                  data-testid="google-signin-btn"
                  onClick={signInWithGoogle}
                  className="w-full inline-flex items-center justify-center gap-2.5 rounded-md border border-hairline bg-white py-3 text-sm font-medium text-ink hover:border-hairline-strong hover:shadow-sm transition-all"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Sign in with Google Workspace
                </button>
              ) : (
                <div data-testid="google-not-configured" className="text-xs text-ink-soft">
                  Google Workspace sign-in isn't configured yet — set GOOGLE_OIDC_CLIENT_ID/SECRET on the backend.
                </div>
              )}
              <p className="text-[11px] text-faint leading-relaxed">Only your organisation's Google Workspace accounts can access this platform.</p>
              <button
                data-testid="show-password-login-link"
                onClick={() => setShowPasswordForm(true)}
                className="text-[11px] text-faint hover:text-brand-dim transition-colors underline underline-offset-2"
              >PM account? Sign in with a password instead →</button>
            </div>
          ) : (
            <>
              {isGoogleMode && (
                <button
                  data-testid="back-to-google-signin-link"
                  onClick={() => setShowPasswordForm(false)}
                  className="mb-4 text-[11px] text-faint hover:text-ink transition-colors"
                >← Back to Google sign-in</button>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">Email</label>
                  <input
                    data-testid="login-email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-[11px] font-medium uppercase tracking-wider text-faint">Password</label>
                    <a href="/forgot-password" data-testid="forgot-password-link" className="text-[11px] text-faint hover:text-brand-dim transition-colors">Forgot password?</a>
                  </div>
                  <input
                    data-testid="login-password"
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
                  />
                </div>
                {err && <div data-testid="login-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
                <button
                  data-testid="login-submit"
                  disabled={busy}
                  className="w-full rounded-md bg-brand py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors"
                >{busy ? "Signing in…" : "Sign in"}</button>
              </form>

              {!isGoogleMode && (
                <div className="mt-7">
                  <div className="mb-2.5 text-[11px] font-medium uppercase tracking-wider text-faint">Quick sign-in (demo roles)</div>
                  <div className="flex flex-wrap gap-2">
                    {QUICK.map((q) => (
                      <button
                        key={q.email}
                        data-testid={`quick-${q.role.toLowerCase()}`}
                        onClick={() => { setEmail(q.email); setPassword(q.pw); }}
                        className="rounded-full border border-hairline px-3 py-1 text-xs text-ink-soft hover:border-brand hover:text-ink transition-colors"
                      >{q.role}</button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
